import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import PageHeader from "../components/PageHeader";
import Breadcrumbs, { buildBreadcrumbSchema } from "../components/Breadcrumbs";
import CTASection from "../components/CTASection";
import { robotCategories } from "../data/robotCategories";
import categoryReferences from "../data/categoryReferences20260922.json";
import { readerGuideByPath } from "../data/readerGuides20261006";

const groups = Array.from(new Set(robotCategories.map((category) => category.group)));

export default function RobotTypes() {
  const breadcrumbs = [
    { label: "Home", to: "/" },
    { label: "Robot Types", to: "/robot-types" }
  ];

  return (
    <>
      <SEO
        title="Complete Robot Types Index"
        description="Robot types including humanoids, robot dogs, drones, AMRs, AGVs, cobots, industrial robots, medical robots, soft robots and inspection robots."
        path="/robot-types"
        schema={buildBreadcrumbSchema(breadcrumbs)}
      />
      <Breadcrumbs items={breadcrumbs} />
      <PageHeader
        eyebrow="Complete robotics taxonomy"
        title="Every major type of robot, organized clearly"
        description="A serious robotics index covering morphology, industrial automation, logistics, healthcare, agriculture, aerial robots, marine robots, space robots, service robots, field robots, transportation, research platforms, and emerging physical AI systems."
      />

      <section className="site-shell py-10 sm:py-14">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {[
            ["All robot sensors", "/robot-sensors", "Vision, LiDAR, radar, IMU, encoders, tactile, force, biosensors, safety sensors."],
            ["Robot components", "/robot-components", "Actuators, motors, gearboxes, batteries, grippers, joints, frames, cooling, connectors."],
            ["Robotics software", "/robotics-software", "ROS, ROS 2, SLAM, control, planning, simulation, robot learning, edge AI."],
            ["Companies", "/robotics-companies", "China, Japan, US, Europe, humanoid startups, drones, medical robotics, warehouse robotics."],
            ["Events", "/robotics-events", "Conferences, humanoid events, AI robotics demos, industrial shows, exhibitions, competitions."]
          ].map(([title, to, text], index) => (
            <Link key={to} to={to} className={`${index === 0 ? "bg-blue-600 text-white" : "bg-white text-neutral-950"} rounded-3xl border border-neutral-200 p-5 transition hover:border-neutral-950`}>
              <p className={`${index === 0 ? "text-neutral-500" : "text-neutral-500"} text-xs font-bold uppercase tracking-[0.2em]`}>Deep page</p>
              <h2 className="mt-3 text-xl font-bold">{title}</h2>
              <p className={`${index === 0 ? "text-neutral-600" : "text-neutral-600"} mt-2 text-sm leading-6`}>{text}</p>
            </Link>
          ))}
        </div>
        <div className="mt-4 soft-card bg-neutral-50/90 p-5">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">Coverage</p>
          <h2 className="mt-3 text-xl font-bold text-neutral-950">{robotCategories.length} robot categories</h2>
          <p className="mt-2 text-sm leading-6 text-neutral-600">Browse robots by physical form, task or operating environment. Each link opens a category guide with its own scope and supporting references.</p>
        </div>
      </section>

      <section className="mx-auto grid max-w-7xl gap-12 px-4 py-8 sm:px-6 lg:px-8">
        {groups.map((group) => {
          const items = robotCategories.filter((category) => category.group === group);
          return (
            <section key={group}>
              <div className="mb-5 border-b border-neutral-200 pb-4">
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-neutral-500">{items.length} categories</p>
                <h2 className="mt-2 text-2xl font-bold tracking-tight text-neutral-950">{group}</h2>
              </div>
              <div className="grid gap-4 lg:grid-cols-2">
                {items.map((category) => (
                  <article key={category.id} className="soft-card p-5 transition hover:border-blue-200 sm:p-6">
                        <h3 className="text-2xl font-bold tracking-tight text-neutral-950">
                          <Link to={`/robot-types/${category.slug}`} className="focus-ring rounded-md">
                            {category.name}
                          </Link>
                        </h3>
                        <p className="mt-3 text-base leading-7 text-neutral-600">
                          {readerGuideByPath[`/robot-types/${category.slug}`]?.description
                            || categoryReferences[category.slug]?.definition
                            || category.shortDescription}
                        </p>
                        <Link to={`/robot-types/${category.slug}`} className="mt-4 inline-flex text-sm font-bold text-neutral-950 underline underline-offset-4">
                          Open category guide
                        </Link>
                  </article>
                ))}
              </div>
            </section>
          );
        })}
      </section>
      <CTASection title="Follow TechniaHQRobot for the complete robotics map" />
    </>
  );
}
